import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ProductImage } from "./ProductImage";
import { StatusBadge } from "./StatusBadge";
import { FormBadge, formulationLine } from "./FormBadge";
import { formatPrice } from "../../lib/format";
import { Certificate } from "@phosphor-icons/react";

/**
 * Card that groups all variants of a single compound family under one tile.
 * Each variant is a crawlable `<Link to="/product/<slug>">` so canonical PDP
 * URLs and SKUs remain intact — this is a purely visual grouping.
 *
 * The card renders:
 *   - Master image + name of the "primary" variant (lowest-price lyophilized
 *     SKU when available, else the solution reference standard)
 *   - Total variant count + a set of variant chips (form badge + strength)
 *   - A single price range summary when >1 variant carries a public price
 */
export function ProductFamilyCard({ family }) {
  const primary = family.primary;
  const variants = family.variants;

  // Default-selected variant for interaction — the primary
  const [selectedSlug, setSelectedSlug] = useState(primary.slug);
  const selected = variants.find((v) => v.slug === selectedSlug) || primary;

  // Price range summary — only from variants with a visible price
  const priced = variants.filter((v) => v.public_price_visible && typeof v.retail_price_cents === "number");
  let priceLine = null;
  if (priced.length > 0) {
    const cents = priced.map((v) => v.retail_price_cents);
    const min = Math.min(...cents);
    const max = Math.max(...cents);
    priceLine = min === max
      ? { display: formatPrice(min, "USD"), range: false }
      : { display: `${formatPrice(min, "USD")} – ${formatPrice(max, "USD")}`, range: true };
  }

  return (
    <article
      className="mps-card group flex flex-col overflow-hidden relative hover:shadow-lg transition-all duration-300"
      data-testid={`family-card-${family.slug}`}
    >
      <Link
        to={`/product/${selected.slug}`}
        className="block aspect-square bg-brand-surface border-b border-brand-border overflow-hidden relative"
        aria-label={`View ${selected.product_name} ${selected.strength}`}
      >
        <ProductImage
          product={selected}
          size="thumbnail"
          padding="p-6"
          className="group-hover:scale-[1.02] transition-transform duration-300"
          testId={`family-image-${family.slug}`}
        />
        <div className="absolute top-3 left-3 flex flex-wrap items-center gap-1.5">
          {variants.length > 1 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-navy bg-white/95 backdrop-blur border border-brand-border rounded-full">
              {variants.length} variants
            </span>
          )}
          <StatusBadge status={selected.status} />
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted">
            {selected.public_sku}
          </div>
          <FormBadge format={selected.format} />
        </div>
        <Link to={`/product/${selected.slug}`} className="block">
          <h3 className="font-display text-[17px] font-semibold text-brand-navy leading-snug tracking-tight min-h-[3.2rem] line-clamp-2 group-hover:text-brand-accent transition-colors">
            {family.display_name}
          </h3>
        </Link>
        <div className="mt-1.5 min-h-[1.25rem] text-[13px] text-brand-muted font-medium" data-testid={`family-formulation-${family.slug}`}>
          {formulationLine(selected)}
        </div>

        {/* Variant chips — always crawlable anchors */}
        {variants.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`family-variants-${family.slug}`}>
            {variants.map((v) => (
              <Link
                key={v.slug}
                to={`/product/${v.slug}`}
                onMouseEnter={() => setSelectedSlug(v.slug)}
                onFocus={() => setSelectedSlug(v.slug)}
                className={
                  "inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono rounded-md border transition-colors " +
                  (v.slug === selectedSlug
                    ? "bg-brand-navy text-white border-brand-navy"
                    : "bg-white text-brand-navy border-brand-border hover:border-brand-navy")
                }
                data-testid={`variant-chip-${v.slug}`}
                aria-label={`View ${v.product_name} ${v.strength}`}
              >
                {v.strength}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 text-[11px] text-brand-muted font-medium">
          {priceLine && (
            <span className="text-brand-navy font-semibold" data-testid={`family-price-${family.slug}`}>
              {priceLine.display}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Certificate size={10} weight="regular" /> Lot-scoped documentation
          </span>
        </div>
      </div>
    </article>
  );
}


/**
 * Bucket products into compound families.
 *
 * Grouping key priority:
 *   1. Non-empty CAS number (shared across variants of the same compound)
 *   2. Normalized slug base (strips trailing strength / reference-standard)
 *
 * The "primary" variant is chosen deterministically:
 *   - Prefer a lyophilized variant with the lowest retail_price_cents
 *   - Else the variant with the lowest retail_price_cents
 *   - Else the first variant alphabetically
 *
 * Products are otherwise never mutated.
 */
export function groupIntoFamilies(products) {
  const _norm = (slug) => {
    return String(slug)
      .replace(/-reference-standard$/, "")
      .replace(/-\d+-mg$/, "")
      .replace(/-\d+-\d+-mg$/, "")
      .replace(/-\d+-iu$/, "");
  };
  const keyOf = (p) => {
    const cas = (p.cas_number || "").trim();
    if (cas && cas !== "N/A" && cas !== "—") return `cas:${cas}`;
    return `slug:${_norm(p.slug)}`;
  };

  const map = new Map();
  for (const p of products) {
    const k = keyOf(p);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(p);
  }

  const families = [];
  for (const [key, variants] of map) {
    const sorted = [...variants].sort((a, b) => {
      const ap = a.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
      const bp = b.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
      if (ap !== bp) return ap - bp;
      return a.slug.localeCompare(b.slug);
    });
    // Primary: cheapest lyophilized if any, else cheapest overall
    const lyoph = sorted.filter((v) => /lyoph/i.test(v.format || ""));
    const primary = lyoph[0] || sorted[0];
    const displayBase = (primary.product_name || "").replace(/\s+\d[\d.,\s]*(mg|mcg|iu|ml|units?|mL|g)$/i, "").trim();
    families.push({
      key,
      slug: _norm(primary.slug),
      display_name: displayBase || primary.product_name,
      primary,
      variants: sorted,
    });
  }
  // Sort families so families with more variants surface higher within their
  // natural order; ties fall back to alphabetical.
  families.sort((a, b) => a.display_name.localeCompare(b.display_name));
  return families;
}
